import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';

import { UserIdentity } from '../models/user-identity';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly platformId = inject(PLATFORM_ID);

  readonly currentIdentity = signal<UserIdentity | null>(this.getIdentity());

  readonly isLoggedIn = computed(() => this.currentIdentity() !== null);

  getItem<T>(key: string, storage?: Storage | null): T | null {
    const targetStorage = storage ?? this.getLocalStorage();

    if (!targetStorage) {
      return null;
    }

    try {
      const item = targetStorage.getItem(key);

      if (item === null) {
        return null;
      }

      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Error reading "${key}" from storage`, error);

      return null;
    }
  }

  setItem<T>(key: string, value: T, storage?: Storage | null): void {
    const targetStorage = storage ?? this.getLocalStorage();

    if (!targetStorage) {
      return;
    }

    try {
      targetStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving "${key}" to storage`, error);
    }
  }

  removeItem(key: string, storage?: Storage | null): void {
    const targetStorage = storage ?? this.getLocalStorage();

    if (!targetStorage) {
      return;
    }

    try {
      targetStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing "${key}" from storage`, error);
    }
  }

  setIdentity(identity: UserIdentity): void {
    this.setItem('identity', identity);
    this.currentIdentity.set(identity);
  }

  getIdentity(): UserIdentity | null {
    return this.getItem<UserIdentity>('identity');
  }

  removeIdentity(): void {
    this.removeItem('identity');
    this.removeVisitedURL();
    this.currentIdentity.set(null);
  }

  setUser<T>(user: T): void {
    this.setItem('user', user);
  }

  getUser<T = unknown>(): T | null {
    return this.getItem<T>('user');
  }

  getUserId(): string | number | null {
    return this.currentIdentity()?.userId ?? null;
  }

  getToken(): string | null {
    return this.currentIdentity()?.accessToken ?? null;
  }

  isAuthenticated(): boolean {
    return this.currentIdentity() !== null;
  }

  getUserName(): string | null {
    return this.currentIdentity()?.userName ?? null;
  }

  getEnvironment(): string | null {
    return this.currentIdentity()?.env ?? null;
  }

  setChangePasswordToken(): void {
    const userId = this.getUserId();

    if (userId === null || userId === undefined) {
      return;
    }

    this.setItem('modal', String(userId), this.getSessionStorage());
  }

  isChangePasswordActive(): boolean {
    const modalUserId = this.getItem<string>('modal', this.getSessionStorage());

    const userId = this.getUserId();

    return (
      modalUserId !== null &&
      userId !== null &&
      userId !== undefined &&
      modalUserId === String(userId)
    );
  }

  removeChangePasswordToken(): void {
    this.removeItem('modal', this.getSessionStorage());
  }

  setVisitedURL(url: string): void {
    this.setItem('visitedURL', url);
  }

  getVisitedURL(): string | null {
    return this.getItem<string>('visitedURL');
  }

  removeVisitedURL(): void {
    this.removeItem('visitedURL');
  }

  clearLocalStorage(): void {
    const storage = this.getLocalStorage();

    if (!storage) {
      return;
    }

    try {
      storage.clear();
    } catch (error) {
      console.error('Error clearing local storage', error);
    }
  }

  clearSessionStorage(): void {
    const storage = this.getSessionStorage();

    if (!storage) {
      return;
    }

    try {
      storage.clear();
    } catch (error) {
      console.error('Error clearing session storage', error);
    }
  }

  clearAll(): void {
    this.clearLocalStorage();
    this.clearSessionStorage();
    this.currentIdentity.set(null);
  }

  private getLocalStorage(): Storage | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      return window.localStorage;
    } catch {
      return null;
    }
  }

  private getSessionStorage(): Storage | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  }
}
